import React, { useEffect, useMemo, useRef } from "react";
import { PopMenu } from "../../shared-components/PopMenu";
import {
  ArrowPathIcon,
  CheckIcon,
  DocumentIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import useRegeneratePDFWithToast from "../../reports/PDFRegenerationWrapper";
import { ReportSummaryItemApi } from "../../api/Reports";
import useNavigateNewPerson from "../../reports/useNavigateNewPerson";
import { useAmendReportWithToast } from "../../reports/OpenAmendmentWrapper";
import {
  useGetReportProductReports,
  useInvalidateReportPendingReviewList,
  useMarkReviewed,
} from "../../api/ReportsApi";
import { useApplicationToast } from "../../webcontent/ApplicationToastProvider";

type Props = {
  reportUUID: string;
  report: ReportSummaryItemApi;
};

const Summary360MenuItems = ({ reportUUID, report }: Props) => {
  const { canRegeneratePDF, canRerunReport, canAmend, canNavPendingReview } =
    usePermissionValidator();
  const reportMenuItems = [];
  const { regeneratePDF } = useRegeneratePDFWithToast();
  const { openAmendment } = useAmendReportWithToast();
  const { showLargeToast } = useApplicationToast();
  const invalidatePendingReview = useInvalidateReportPendingReviewList();
  const {
    data: productReports,
    mutate: productReportsGet,
    isLoading: productReportsLoading,
  } = useGetReportProductReports();
  const { mutateAsync: markReviewed, isLoading: markingReviewed } =
    useMarkReviewed();
  const completeReviewInFlight = useRef(false);

  const { navigateCreatePerson } = useNavigateNewPerson();

  useEffect(() => {
    productReportsGet(reportUUID);
  }, [reportUUID, productReportsGet]);

  const unreviewedProductReportUuids = useMemo(() => {
    if (!productReports?.length) return [];
    return productReports
      .filter((pr) => !pr.reviewed && pr.external_id)
      .map((pr) => pr.external_id as string);
  }, [productReports]);
  if (canRegeneratePDF) {
    reportMenuItems.push({
      component: (
        <div className="text-gray-500 flex gap-3">
          <DocumentIcon className="w-5 h-5" />
          Regenerate PDF
        </div>
      ),
      onClick: () => {
        regeneratePDF(reportUUID);
      },
    });
  }
  if (canRerunReport) {
    reportMenuItems.push({
      component: (
        <div className="text-gray-500 flex gap-3">
          <ArrowPathIcon className="w-5 h-5" />
          Rerun Report
        </div>
      ),
      onClick: () => {
        navigateCreatePerson({
          state: { rerun: true, parent_report: report },
        });
      },
    });
  }
  if (canAmend) {
    reportMenuItems.push({
      component: (
        <div className="text-gray-500 flex gap-3">
          <ShieldExclamationIcon className="w-5 h-5" />
          Amend Report
        </div>
      ),
      onClick: () => {
        openAmendment(reportUUID);
      },
    });
  }
  if (
    canNavPendingReview &&
    !productReportsLoading &&
    unreviewedProductReportUuids.length > 0
  ) {
    reportMenuItems.push({
      component: (
        <div
          className={`text-gray-500 flex gap-3 ${
            markingReviewed ? "opacity-50" : ""
          }`}
        >
          <CheckIcon className="w-5 h-5" />
          Complete Review
        </div>
      ),
      onClick: () => {
        if (
          completeReviewInFlight.current ||
          markingReviewed ||
          unreviewedProductReportUuids.length === 0
        ) {
          return;
        }
        completeReviewInFlight.current = true;
        void (async () => {
          try {
            await markReviewed({
              product_reports_uuids: unreviewedProductReportUuids,
            });
            invalidatePendingReview();
            productReportsGet(reportUUID);
            showLargeToast?.({
              title: "Review complete",
              message:
                "Product reports for this check are marked reviewed. Pending review lists will update shortly.",
              icon: (
                <div className="bg-green-100 text-green-600 rounded-full p-2">
                  <CheckIcon className="w-6" />
                </div>
              ),
            });
          } catch {
            showLargeToast?.({
              title: "Could not mark reviewed",
              message:
                "Something went wrong. Try again or contact support if this continues.",
              icon: (
                <div className="bg-red-100 text-red-600 rounded-full p-2">
                  <ExclamationTriangleIcon className="w-6" />
                </div>
              ),
            });
          } finally {
            completeReviewInFlight.current = false;
          }
        })();
      },
    });
  }

  return (
    <PopMenu
      button={<EllipsisVerticalIcon className="w-10 h-10 text-gray-500" />}
      menuItems={reportMenuItems}
    />
  );
};

export default Summary360MenuItems;
